import { lazy } from 'react';

// project import
import { CalendarStatus } from 'common/dto-types/calendar-dto';
import { MessageStatus } from 'common/dto-types/message-dto';
import Loadable from 'components/Loadable';
import MainLayout from 'layout/MainLayout';
import CalendarEventHistoryPage from 'pages/calendar/event-history-page';
import ContactTypeListPage from 'pages/contact-types-list-page';
import ContactDetailsPage from 'pages/contact/details-page';
import JobTitleListPage from 'pages/job-titles-list-page';
import MessageListPage from 'pages/message/list-page';
import { Outlet } from 'react-router';
import AuthGuard from 'utils/route-guard/AuthGuard';
import GroupGuard from 'utils/route-guard/GroupGuard';
import { CALENDAR_MEDIA_OFFICERS } from 'utils/route-guard/roles-check';
import ClearCacheButton from '../components/ClearCacheButton';
import TeamListPage from '../pages/teams-list-page';
import VenueListPage from '../pages/venue-list-page';
import VenueTypeListPage from '../pages/venue-type-list-page';
import CityListPage from '../pages/city-list-page';
import MessageDetailsPage from 'pages/message/detail-page';
import SynchronizeUserGroupsButton from 'components/UserGroupSynchronization/SynchronizeUserGroupsButton';
import MessageHistoryPage from 'pages/message/message-history-page';

// pages routing
const MaintenanceError = Loadable(lazy(() => import('pages/maintenance/404')));
const UserListPage = Loadable(lazy(() => import('pages/user-list-page')));
const CountryListPage = Loadable(lazy(() => import('pages/country-list-page')));
const AccreditationListPage = Loadable(lazy(() => import('pages/accreditation-list-page')));
const CalendarListPage = Loadable(lazy(() => import('pages/calendar/list-page')));
const CalendarDetailsPage = Loadable(lazy(() => import('pages/calendar/details-page')));
const ContactListPage = Loadable(lazy(() => import('pages/contact/list-page')));
const ContactCategoryListPage = Loadable(lazy(() => import('pages/contact-categories-list-page')));
const EventListPage = Loadable(lazy(() => import('pages/event-list-page')));

// ==============================|| MAIN ROUTING ||============================== //

const MainRoutes = {
    path: '/',
    errorElement: <MaintenanceError />,
    children: [
        {
            path: '/',
            element: (
                <AuthGuard>
                    <MainLayout />
                </AuthGuard>
            ),
            children: [
                {
                    path: '/dashboard',
                    element: (
                        <div>
                            Welcom to FIFA Back Office!.
                            <br />
                            Select an Item on the left to start working.
                            <br />
                            <div>
                                <ClearCacheButton />
                                <SynchronizeUserGroupsButton />
                            </div>
                        </div>
                    ),
                },
                {
                    path: '/',
                    element: (
                        <GroupGuard allowedGroups={[]}>
                            <Outlet />
                        </GroupGuard>
                    ),
                    children: [
                        {
                            path: 'user',
                            children: [
                                {
                                    path: 'all',
                                    element: <UserListPage />,
                                },
                            ],
                        },
                        {
                            path: 'accreditation',
                            children: [
                                {
                                    path: 'all',
                                    element: <AccreditationListPage />,
                                },
                            ],
                        },
                        {
                            path: 'country',
                            children: [
                                {
                                    path: 'all',
                                    element: <CountryListPage />,
                                },
                            ],
                        },
                        {
                            path: 'venue',
                            children: [
                                {
                                    path: 'all',
                                    element: <VenueListPage />,
                                },
                            ],
                        },
                        {
                            path: 'venue-type',
                            children: [
                                {
                                    path: 'all',
                                    element: <VenueTypeListPage />,
                                },
                            ],
                        },
                        {
                            path: 'city',
                            children: [
                                {
                                    path: 'all',
                                    element: <CityListPage />,
                                },
                            ],
                        },
                        {
                            path: 'team',
                            children: [
                                {
                                    path: 'all',
                                    element: <TeamListPage />,
                                },
                            ],
                        },
                        {
                            path: 'job-title',
                            children: [
                                {
                                    path: 'all',
                                    element: <JobTitleListPage />,
                                },
                            ],
                        },
                        {
                            path: 'contact-type',
                            children: [
                                {
                                    path: 'all',
                                    element: <ContactTypeListPage />,
                                },
                            ],
                        },
                        {
                            path: 'contact-category',
                            children: [
                                {
                                    path: 'all',
                                    element: <ContactCategoryListPage />,
                                },
                            ],
                        },
                    ],
                },
                {
                    path: 'calendar',
                    element: (
                        <GroupGuard allowedGroups={[CALENDAR_MEDIA_OFFICERS]}>
                            <Outlet />
                        </GroupGuard>
                    ),
                    children: [
                        {
                            path: 'all',
                            element: <CalendarListPage key={CalendarStatus.Active} status={CalendarStatus.Active} />,
                        },
                        {
                            path: 'archived',
                            element: (
                                <CalendarListPage key={CalendarStatus.Archived} status={CalendarStatus.Archived} />
                            ),
                        },
                        {
                            path: ':id/events',
                            element: <EventListPage />,
                        },
                        {
                            path: 'details/:id',
                            element: <CalendarDetailsPage />,
                        },
                        {
                            path: 'details/:id/events/:eventId',
                            element: <CalendarDetailsPage />,
                        },
                        {
                            path: 'history/:id',
                            element: (
                                <GroupGuard allowedGroups={[]}>
                                    <CalendarEventHistoryPage />
                                </GroupGuard>
                            ),
                        },
                        {
                            path: 'history/:id/event/:eventId',
                            element: (
                                <GroupGuard allowedGroups={[]}>
                                    <CalendarEventHistoryPage />
                                </GroupGuard>
                            ),
                        },
                    ],
                },
                {
                    path: 'contact',
                    element: (
                        <GroupGuard allowedGroups={[]}>
                            <Outlet />
                        </GroupGuard>
                    ),
                    children: [
                        {
                            path: 'all',
                            element: <ContactListPage />,
                        },
                        {
                            path: 'details',
                            children: [
                                {
                                    path: 'add-new',
                                    element: <ContactDetailsPage />,
                                },
                                {
                                    path: ':id',
                                    element: <ContactDetailsPage />,
                                },
                            ],
                        },
                    ],
                },
                {
                    path: 'message',
                    element: (
                        <GroupGuard allowedGroups={[]}>
                            <Outlet />
                        </GroupGuard>
                    ),
                    children: [
                        {
                            path: 'draft',
                            element: (
                                <MessageListPage
                                    key="message-list-draft"
                                    statuses={[MessageStatus.Draft]}
                                    allowStatusFilter={false}
                                />
                            ),
                        },
                        {
                            path: 'live',
                            element: (
                                <MessageListPage
                                    key="message-list-live"
                                    statuses={[
                                        MessageStatus.Scheduled,
                                        MessageStatus.Publishing,
                                        MessageStatus.Published,
                                    ]}
                                    allowStatusFilter={true}
                                />
                            ),
                        },
                        {
                            path: 'archived',
                            element: (
                                <MessageListPage
                                    key="message-list-archived"
                                    statuses={[MessageStatus.Removed, MessageStatus.Recalled]}
                                    allowStatusFilter={true}
                                />
                            ),
                        },
                        {
                            path: 'history',
                            element: <MessageHistoryPage key="message-list-history" />,
                        },
                        {
                            path: 'history/id/:messageId',
                            element: <MessageHistoryPage key="message-list-history" />,
                        },
                        {
                            path: 'detail',
                            children: [
                                {
                                    path: 'new',
                                    element: <MessageDetailsPage />,
                                },
                                {
                                    path: ':id',
                                    element: <MessageDetailsPage />,
                                },
                            ],
                        },
                    ],
                },
            ],
        },
    ],
};

export default MainRoutes;
